import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled, { ThemeProvider } from 'styled-components';
import darkTheme from 'themes/darkTheme';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import AddressForm from '../components/AddressForm';
import ProgressHeader from '../components/ProgressHeader';
import { useSignup } from '../context/SignupContext';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  padding: 32px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const CardSubtitle = styled.p`
  font-size: 15px;
  color: #94a3b8;
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  button {
    width: auto;
    min-width: 140px;
    flex: 0 0 auto;
  }

  @media (max-width: 768px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const MailingAddressStep = () => {
  const { formData, updateField, errors, getTotalSteps, getStepNumber } =
    useSignup();
  const history = useHistory();
  const [sameAsShipping, setSameAsShipping] = useState(false);

  const handleAddressChange = (field, value) => {
    updateField('mailing_address', {
      ...formData.mailing_address,
      [field]: value,
    });
  };

  const handleAddressSelect = (parsed) => {
    updateField('mailing_address', {
      ...formData.mailing_address,
      ...parsed,
    });
  };

  const handleSameAsToggle = (checked) => {
    setSameAsShipping(checked);
    if (checked) {
      // Copy shipping address to mailing address
      updateField('mailing_address', { ...formData.shipping_address });
    } else {
      // Clear mailing address
      updateField('mailing_address', {});
    }
  };

  const isFormValid =
    sameAsShipping ||
    (formData.mailing_address?.street1 &&
      formData.mailing_address?.city &&
      formData.mailing_address?.state &&
      formData.mailing_address?.zipcode);

  const handleContinue = () => {
    if (!sameAsShipping) {
      const address = formData.mailing_address || {};

      // Validate required fields
      if (
        !address.street1 ||
        !address.city ||
        !address.state ||
        !address.zipcode
      ) {
        toast.error('Please fill in all required address fields');
        return;
      }
    }

    // Navigate to next step
    history.push('/promoter-signup/terms');
  };

  const handleBack = () => {
    history.push('/promoter-signup/shipping-address');
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <PageContainer>
        <ContentWrapper>
          <ProgressHeader
            currentStep={getStepNumber('/promoter-signup/mailing-address')}
            totalSteps={getTotalSteps()}
            stepTitle="Mailing Address"
          />

          <Card>
            <CardSubtitle>
              Where should we send important documents and correspondence?
            </CardSubtitle>

            <AddressForm
              address={formData.mailing_address || {}}
              onChange={handleAddressChange}
              onAddressSelect={handleAddressSelect}
              errors={errors.mailing_address || {}}
              showSameAsCheckbox={true}
              sameAsChecked={sameAsShipping}
              onSameAsToggle={handleSameAsToggle}
              idPrefix="mailing_"
            />

            <ButtonRow>
              <ButtonV2 onClick={handleBack}>← Back</ButtonV2>
              <ButtonV2
                onClick={handleContinue}
                disabled={!isFormValid}
                primary={!!isFormValid}
              >
                Continue →
              </ButtonV2>
            </ButtonRow>
          </Card>
        </ContentWrapper>
      </PageContainer>
    </ThemeProvider>
  );
};

export default MailingAddressStep;
